import { AnnotationType, CanvasCursor, DragMode, GetCanvasCursorParams } from '@v2/components/imageHandling/components/DrawingCanvas/types';
import { ERASER_TOOL, POINTER_TOOL } from '@v2/components/imageHandling/components/DrawingCanvas/constants';

/**
 * Resolves the CSS cursor for the drawing overlay based on the active tool
 * and current hover / drag state. Web-only — native ignores cursor values.
 */
export const getCanvasCursor = ({
  dragMode,
  activeTool,
  hoveredHandle,
  hoveredAnnotationId,
  hasSelection,
  isOverSelectionBody,
  isZoomed,
  isPanning,
}: GetCanvasCursorParams): CanvasCursor => {
  // Active drag states take precedence regardless of tool.
  if (dragMode === DragMode.Move) return CanvasCursor.Grabbing;
  if (dragMode === DragMode.Resize) return hoveredHandle?.cursor ?? CanvasCursor.Grab;
  if (dragMode === DragMode.Draw) return CanvasCursor.Crosshair;

  // Pointer tool: selection / move / resize affordances. Empty space gets grab/grabbing when the
  // image is zoomed in so the user can pan it (mirrors ProgressiveImageWeb's cursor behaviour).
  if (activeTool === POINTER_TOOL) {
    if (hoveredHandle) return hoveredHandle.cursor;
    if (hasSelection && isOverSelectionBody) return CanvasCursor.Grab;
    if (hoveredAnnotationId) return CanvasCursor.Pointer;
    if (isPanning) return CanvasCursor.Grabbing;
    if (isZoomed) return CanvasCursor.Grab;
    return CanvasCursor.Default;
  }

  // Eraser: red strikethrough on hover over an annotation, disallowed on empty space.
  if (activeTool === ERASER_TOOL) {
    return hoveredAnnotationId ? CanvasCursor.Default : CanvasCursor.NotAllowed;
  }

  // Drawing tools: always show the drawing affordance regardless of what's underneath.
  switch (activeTool) {
    case AnnotationType.Text:
      return CanvasCursor.Text;
    case AnnotationType.Pen:
    case AnnotationType.Highlighter:
    case AnnotationType.Arrow:
    case AnnotationType.Rectangle:
    case AnnotationType.Circle:
      return CanvasCursor.Crosshair;
    default:
      return CanvasCursor.Default;
  }
};
